import { BANDS, type AudioFeatures } from '../audioEngine';
import { SOURCE_OPTIONS, type BuddhaConfig, type LightLevels } from '../buddha';
import { FONT_OPTIONS, getPath, setPath } from '../look';
import type { Look, ScenePreset } from '../types';
import { h, ICON } from './dom';

type Ctl =
  | { type: 'range'; path: string; label: string; min: number; max: number; step: number; unit?: string }
  | { type: 'color'; path: string; label: string }
  | { type: 'select'; path: string; label: string; options: { label: string; value: string }[] }
  | { type: 'toggle'; path: string; label: string };

interface Section {
  id: string;
  title: string;
  open?: boolean;
  /** 标题旁的说明 */
  note?: string;
  /** 主尊光源分组：显示「恢复默认」 */
  resetGroup?: keyof BuddhaConfig;
  controls?: Ctl[];
  custom?: (body: HTMLElement) => void;
}

export interface AppSettings {
  volume: number;
  lyricOffset: number;
  presetId: string; // 'track' = 跟随曲目
  repeat: 'one' | 'all' | 'none';
}

export interface PanelHost {
  look(): Look;
  settings(): AppSettings;
  onLookChange(path: string): void;
  onSettingChange(key: keyof AppSettings): void;
  buddha(): BuddhaConfig;
  onBuddhaChange(path: string): void;
  resetBuddha(group?: Exclude<keyof BuddhaConfig, 'size'>): void;
  presets(): ScenePreset[];
  trackSceneName(): string;
  selectPreset(id: string): void;
  savePresetAs(name: string): void;
  overwritePreset(): void;
  deletePreset(): void;
  importPresets(): void;
  exportCurrent(): void;
  exportAll(): void;
  openTimelineEditor(): void;
  timelineInfo(): string;
  importAudio(): void;
}

const r = (path: string, label: string, min: number, max: number, step: number, unit?: string): Ctl => ({ type: 'range', path, label, min, max, step, unit });
const c = (path: string, label: string): Ctl => ({ type: 'color', path, label });
const t = (path: string, label: string): Ctl => ({ type: 'toggle', path, label });
const s = (path: string, label: string, options: { label: string; value: string }[]): Ctl => ({ type: 'select', path, label, options });

/** 设计 / 调试面板 */
export class Panel {
  readonly el: HTMLElement;
  private inputs: { ctl: Ctl; sync: () => void }[] = [];
  private meterEls: { fill: HTMLElement; val: HTMLElement }[] = [];
  private metaEl!: HTMLElement;
  private presetSelect!: HTMLSelectElement;
  private tlInfo!: HTMLElement;

  constructor(private readonly host: PanelHost) {
    this.el = h('aside', { id: 'panel', class: 'panel', 'aria-label': '设计与调试面板' });
    const header = h(
      'div',
      { class: 'panel-head' },
      h('div', { class: 'panel-title' }, '设计 · 调试'),
      h('button', { class: 'icon-btn', title: '收起面板 (D)', html: ICON.close, onclick: () => this.toggle(false) }),
    );
    const body = h('div', { class: 'panel-body' });
    for (const sec of this.sections()) body.appendChild(this.buildSection(sec));
    this.el.append(header, body);
    // 面板内的操作不应冒泡成「点击画面」
    this.el.addEventListener('pointerdown', (e) => e.stopPropagation());
  }

  get open(): boolean {
    return this.el.classList.contains('open');
  }

  toggle(force?: boolean): void {
    const open = force ?? !this.open;
    this.el.classList.toggle('open', open);
    document.body.classList.toggle('panel-open', open);
    if (open) this.syncAll();
  }

  private sections(): Section[] {
    const H = this.host;
    return [
      { id: 'preset', title: '场景预设', open: true, custom: (b) => this.buildPresets(b) },
      { id: 'monitor', title: '实时监测', open: true, custom: (b) => this.buildMonitor(b) },
      {
        id: 'buddha',
        title: '佛像',
        open: true,
        custom: (b) => {
          b.appendChild(this.buildControl(r('buddha.size', '佛像大小（100% = 最大且完整）', 0.4, 1, 0.01)));
          b.append(
            h('div', { class: 'hint' }, '佛像始终水平居中、完整呈现；缩放以佛像中心为基准。以下五路光源各自独立开关、调节，并可选择跟随哪一路音频特征。所有调整即时生效并保存在本机。'),
            h('div', { class: 'btn-row' }, h('button', { class: 'btn', onclick: () => H.resetBuddha() }, '全部恢复默认')),
          );
        },
      },
      {
        id: 'head',
        title: '头部圆光',
        open: true,
        resetGroup: 'head',
        controls: [
          t('buddha.head.on', '开启头部圆光'),
          r('buddha.head.radius', '圆光大小', 0.06, 0.24, 0.001),
          r('buddha.head.intensity', '圆光亮度', 0, 2.5, 0.01),
          r('buddha.head.rings', '圆环层次', 0, 1.5, 0.01),
          r('buddha.head.offsetY', '圆心上下微调（以额头为准）', -0.03, 0.03, 0.0005),
          c('buddha.head.color', '圆光颜色'),
          r('buddha.head.rayCount', '远伸光线 · 数量（0 = 关）', 0, 24, 1),
          r('buddha.head.rayIntensity', '远伸光线 · 亮度', 0, 1.5, 0.01),
          r('buddha.head.rayWidth', '远伸光线 · 宽度', 0.4, 3, 0.01),
          r('buddha.head.rayLength', '远伸光线 · 长度', 0.2, 2.5, 0.01),
          s('buddha.head.source', '音频驱动来源', SOURCE_OPTIONS),
          r('buddha.head.react', '音频响应强度', 0, 2, 0.01),
        ],
      },
      {
        id: 'aura',
        title: '周身金白光',
        resetGroup: 'aura',
        controls: [
          t('buddha.aura.on', '开启周身金白光'),
          r('buddha.aura.intensity', '亮度', 0, 2.5, 0.01),
          r('buddha.aura.spread', '舒展范围', 0.4, 2.2, 0.01),
          r('buddha.aura.flow', '宽柔光带', 0, 1, 0.01),
          r('buddha.aura.inner', '身内透光（过高会减弱衣饰细节）', 0, 0.6, 0.01),
          c('buddha.aura.color', '光色'),
          s('buddha.aura.source', '音频驱动来源', SOURCE_OPTIONS),
          r('buddha.aura.react', '音频响应强度', 0, 2, 0.01),
        ],
      },
      {
        id: 'contour',
        title: '佛身轮廓光',
        resetGroup: 'contour',
        controls: [
          t('buddha.contour.on', '开启佛身轮廓光'),
          r('buddha.contour.intensity', '光边亮度', 0, 2.5, 0.01),
          r('buddha.contour.width', '光边宽度', 0, 1, 0.01),
          r('buddha.contour.inner', '内缘透光', 0, 1.5, 0.01),
          c('buddha.contour.color', '光边颜色'),
          s('buddha.contour.source', '音频驱动来源', SOURCE_OPTIONS),
          r('buddha.contour.react', '音频响应强度', 0, 2, 0.01),
        ],
      },
      {
        id: 'urna',
        title: '眉间白毫柔光',
        resetGroup: 'urna',
        controls: [
          t('buddha.urna.on', '开启白毫柔光'),
          r('buddha.urna.intensity', '柔光亮度', 0, 1.6, 0.01),
          r('buddha.urna.size', '光辉大小', 0.4, 2.5, 0.01),
          c('buddha.urna.color', '光色'),
          s('buddha.urna.source', '音频驱动来源', SOURCE_OPTIONS),
          r('buddha.urna.react', '音频响应强度', 0, 2, 0.01),
        ],
      },
      {
        id: 'crown',
        title: '佛顶光',
        resetGroup: 'crown',
        controls: [
          t('buddha.crown.on', '开启佛顶光晕'),
          r('buddha.crown.intensity', '光晕亮度', 0, 2, 0.01),
          r('buddha.crown.size', '光晕大小', 0.4, 3, 0.01),
          r('buddha.crown.beam', '佛顶放光（0 = 关）', 0, 1.5, 0.01),
          c('buddha.crown.color', '光色'),
          s('buddha.crown.source', '音频驱动来源', SOURCE_OPTIONS),
          r('buddha.crown.react', '音频响应强度', 0, 2, 0.01),
        ],
      },
      {
        id: 'bg',
        title: '背景',
        controls: [
          c('background.top', '上部色'),
          c('background.bottom', '下部色'),
          r('background.nebula', '云气', 0, 1.5, 0.01),
          r('background.nebulaScale', '云气尺度', 0.4, 4, 0.05),
          r('background.vignette', '暗角', 0, 1, 0.01),
          r('render.exposure', '曝光', 0.3, 2.5, 0.01),
          r('render.scale', '渲染精度', 0.35, 1, 0.05),
        ],
      },
      { id: 'color', title: '主色 / 辅色', open: true, controls: [c('colors.primary', '主色（佛光）'), c('colors.secondary', '辅色'), c('colors.accent', '点缀色（光环）')] },
      {
        id: 'halo',
        title: '背景 · 环境光晕',
        controls: [
          r('halo.x', '光心 X（屏幕比例）', 0, 1, 0.005),
          r('halo.y', '光心 Y', 0, 1, 0.005),
          r('halo.radius', '光晕半径', 0.03, 0.6, 0.005),
          r('halo.intensity', '辉光强度', 0, 2.5, 0.01),
          r('halo.bloom', '外圈泛光', 0, 2, 0.01),
          r('halo.mandorla', '舟形背光', 0, 1, 0.01),
        ],
      },
      {
        id: 'rays',
        title: '背景 · 光束',
        controls: [
          r('rays.count', '光束数量', 0, 120, 1),
          r('rays.intensity', '光束强度', 0, 2, 0.01),
          r('rays.sharpness', '光束锐度', 1, 40, 0.5),
          r('rays.speed', '旋转速度', -0.2, 0.2, 0.001),
          r('rays.length', '光束长度', 0.1, 2, 0.01),
          r('rays.shimmer', '闪烁', 0, 2, 0.01),
        ],
      },
      {
        id: 'rings',
        title: '背景 · 光环',
        controls: [
          r('rings.count', '光环数量', 0, 12, 1),
          r('rings.intensity', '光环强度', 0, 2, 0.01),
          r('rings.spacing', '间距', 0.02, 0.2, 0.001),
          r('rings.width', '线宽', 0.001, 0.03, 0.0005),
          r('rings.speed', '外扩速度', 0, 0.1, 0.001),
          r('rings.petals', '莲瓣数', 0, 32, 1),
          r('rings.petalDepth', '莲瓣深度', 0, 0.3, 0.005),
          r('rings.pulse', '脉冲幅度', 0, 2, 0.01),
        ],
      },
      {
        id: 'particles',
        title: '粒子',
        controls: [
          s('particles.mode', '类型', [
            { label: '光尘 · 飘升', value: 'motes' },
            { label: '花雨 · 莲瓣飘落', value: 'petals' },
            { label: '放射 · 自光心散出', value: 'radiate' },
          ]),
          r('particles.count', '数量', 0, 600, 1),
          r('particles.size', '大小', 0.2, 4, 0.05),
          r('particles.speed', '速度', 0, 3, 0.01),
          r('particles.intensity', '亮度', 0, 2, 0.01),
          r('particles.spread', '分布范围', 0.1, 1.5, 0.01),
        ],
      },
      {
        id: 'text',
        title: '歌词文字（竖排）',
        controls: [
          s('text.font', '字体', FONT_OPTIONS),
          r('text.weight', '字重', 300, 900, 100),
          s('text.layout', '排布', [
            { label: '右上边缘竖排（长句自动改两侧对联）', value: 'right' },
            { label: '两侧对联（前半句右、后半句左）', value: 'couplet' },
          ]),
          r('text.size', '期望字号（空间不足时自动缩小）', 2.5, 9, 0.1, 'vh'),
          r('text.spacing', '字距', 0, 1, 0.01, 'em'),
          r('text.margin', '距屏幕边缘', 0, 10, 0.1, 'vw'),
          c('text.color', '文字色'),
          c('text.glowColor', '辉光色'),
          r('text.glow', '文字辉光', 0, 2, 0.01),
          s('text.animation', '入场动画', [
            { label: '绽放（放大 + 辉光）', value: 'bloom' },
            { label: '淡入', value: 'fade' },
            { label: '上升', value: 'rise' },
            { label: '逐字', value: 'stagger' },
            { label: '墨晕（逐字由虚到实）', value: 'ink' },
          ]),
          r('text.duration', '动画时长', 0.2, 3, 0.05, 's'),
        ],
      },
      {
        id: 'lyrics',
        title: '歌词时间轴',
        custom: (b) => {
          b.appendChild(this.buildControl({ type: 'range', path: 'settings.lyricOffset', label: '全局时间偏移', min: -3, max: 3, step: 0.01, unit: 's' }));
          this.tlInfo = h('div', { class: 'hint' });
          b.append(
            this.tlInfo,
            h('div', { class: 'btn-row' }, h('button', { class: 'btn', onclick: () => H.openTimelineEditor() }, '打开时间轴编辑器')),
            h('div', { class: 'hint' }, '偏移为正：歌词晚于音频出现。单曲的精细偏移写在时间轴文件的 offset 中。'),
          );
        },
      },
      {
        id: 'reactive',
        title: '音频驱动',
        open: true,
        controls: [
          s('reactive.mode', '驱动方式', [
            { label: '响度包络', value: 'loudness' },
            { label: '频段能量', value: 'bands' },
            { label: '混合', value: 'mix' },
          ]),
          r('reactive.mix', '混合比例（频段占比）', 0, 1, 0.01),
          r('reactive.sensitivity', '灵敏度', 0.1, 4, 0.01),
          r('reactive.threshold', '阈值（门限）', 0, 0.9, 0.01),
          r('reactive.attack', '平滑 · 起音', 1, 500, 1, 'ms'),
          r('reactive.release', '平滑 · 释放', 10, 3000, 5, 'ms'),
          t('reactive.agc', '自动增益（适应不同录音音量）'),
          ...BANDS.map((b) => r(`reactive.${b.key}`, `频段权重 · ${b.label} ${b.lo < 1000 ? b.lo : b.lo / 1000 + 'k'}–${b.hi < 1000 ? b.hi : b.hi / 1000 + 'k'}Hz`, 0, 2, 0.01)),
          r('reactive.toGlow', '影响 → 辉光', 0, 2, 0.01),
          r('reactive.toRays', '影响 → 光束', 0, 2, 0.01),
          r('reactive.toRings', '影响 → 光环', 0, 2, 0.01),
          r('reactive.toParticles', '影响 → 粒子', 0, 2, 0.01),
          r('reactive.toText', '影响 → 文字辉光', 0, 2, 0.01),
          r('reactive.lyricPulse', '换句光脉冲', 0, 2, 0.01),
        ],
      },
      {
        id: 'volume',
        title: '音量与音频',
        custom: (b) => {
          b.appendChild(this.buildControl({ type: 'range', path: 'settings.volume', label: '音量', min: 0, max: 1, step: 0.01 }));
          b.append(
            h('div', { class: 'btn-row' }, h('button', { class: 'btn', onclick: () => H.importAudio() }, '导入本地音频 / 时间轴…')),
            h('div', { class: 'hint' }, '本地文件只在当前浏览器会话中播放，不会上传。同名的 .json / .lrc 会作为该曲时间轴。'),
          );
        },
      },
    ];
  }

  private buildSection(sec: Section): HTMLElement {
    const body = h('div', { class: 'sec-body' });
    if (sec.custom) sec.custom(body);
    for (const ctl of sec.controls ?? []) body.appendChild(this.buildControl(ctl));
    if (sec.resetGroup) {
      const g = sec.resetGroup as Exclude<keyof BuddhaConfig, 'size'>;
      body.appendChild(h('div', { class: 'btn-row' }, h('button', { class: 'btn', onclick: () => this.host.resetBuddha(g) }, '恢复此项默认')));
    }
    const d = h('details', { class: 'sec', open: !!sec.open }, h('summary', {}, sec.title), body);
    return d;
  }

  private read(path: string): unknown {
    if (path.startsWith('settings.')) return this.host.settings()[path.slice(9) as keyof AppSettings];
    if (path.startsWith('buddha.')) return getPath(this.host.buddha(), path.slice(7));
    return getPath(this.host.look(), path);
  }

  private write(path: string, v: unknown): void {
    if (path.startsWith('settings.')) {
      const key = path.slice(9) as keyof AppSettings;
      (this.host.settings() as unknown as Record<string, unknown>)[key] = v;
      this.host.onSettingChange(key);
    } else if (path.startsWith('buddha.')) {
      setPath(this.host.buddha(), path.slice(7), v);
      this.host.onBuddhaChange(path.slice(7));
    } else {
      setPath(this.host.look(), path, v);
      this.host.onLookChange(path);
    }
  }

  private buildControl(ctl: Ctl): HTMLElement {
    const id = 'ctl-' + ctl.path.replace(/\./g, '-');
    const row = h('div', { class: `ctl ctl-${ctl.type}` });
    const label = h('label', { for: id }, ctl.label);
    let sync: () => void;
    if (ctl.type === 'range') {
      const input = h('input', { id, type: 'range', min: ctl.min, max: ctl.max, step: ctl.step });
      const num = h('input', { type: 'number', class: 'num', min: ctl.min, max: ctl.max, step: ctl.step, 'aria-label': ctl.label });
      const fmt = (v: number) => (ctl.step >= 1 ? String(Math.round(v)) : String(+v.toFixed(4)));
      sync = () => {
        const v = Number(this.read(ctl.path));
        input.value = String(v);
        num.value = fmt(v);
      };
      input.addEventListener('input', () => {
        num.value = fmt(Number(input.value));
        this.write(ctl.path, Number(input.value));
      });
      num.addEventListener('change', () => {
        const v = Number(num.value);
        if (!Number.isFinite(v)) return sync();
        input.value = String(v);
        this.write(ctl.path, v);
      });
      row.append(h('div', { class: 'ctl-top' }, label, h('span', { class: 'num-wrap' }, num, ctl.unit ? h('span', { class: 'unit' }, ctl.unit) : null)), input);
    } else if (ctl.type === 'color') {
      const input = h('input', { id, type: 'color' });
      const hex = h('input', { type: 'text', class: 'hex', maxlength: 7, 'aria-label': ctl.label + '（十六进制）' });
      sync = () => {
        const v = String(this.read(ctl.path));
        input.value = v;
        hex.value = v;
      };
      input.addEventListener('input', () => {
        hex.value = input.value;
        this.write(ctl.path, input.value);
      });
      hex.addEventListener('change', () => {
        if (/^#[0-9a-f]{6}$/i.test(hex.value)) {
          input.value = hex.value;
          this.write(ctl.path, hex.value.toLowerCase());
        } else sync();
      });
      row.append(label, h('span', { class: 'color-wrap' }, hex, input));
    } else if (ctl.type === 'select') {
      const sel = h('select', { id }, ...ctl.options.map((o) => h('option', { value: o.value }, o.label)));
      sync = () => {
        sel.value = String(this.read(ctl.path));
      };
      sel.addEventListener('change', () => this.write(ctl.path, sel.value));
      row.append(label, sel);
    } else {
      const input = h('input', { id, type: 'checkbox', class: 'switch' });
      sync = () => {
        input.checked = !!this.read(ctl.path);
      };
      input.addEventListener('change', () => this.write(ctl.path, input.checked));
      row.append(label, input);
    }
    this.inputs.push({ ctl, sync });
    return row;
  }

  private buildPresets(b: HTMLElement): void {
    const H = this.host;
    this.presetSelect = h('select', { id: 'preset-select', 'aria-label': '场景预设' });
    this.presetSelect.addEventListener('change', () => H.selectPreset(this.presetSelect.value));
    const nameInput = h('input', { type: 'text', placeholder: '新预设名称', class: 'text' });
    b.append(
      this.presetSelect,
      h('div', { class: 'hint' }, '「跟随曲目」时，切换曲目会载入该曲/播单指定的场景，未保存的调整会被替换；选定某个预设后，所有曲目都使用它。'),
      h(
        'div',
        { class: 'btn-row' },
        nameInput,
        h('button', { class: 'btn primary', onclick: () => { H.savePresetAs(nameInput.value.trim()); nameInput.value = ''; } }, '另存为'),
      ),
      h(
        'div',
        { class: 'btn-row' },
        h('button', { class: 'btn', onclick: () => H.overwritePreset() }, '保存到当前预设'),
        h('button', { class: 'btn', onclick: () => H.deletePreset() }, '删除'),
      ),
      h(
        'div',
        { class: 'btn-row' },
        h('button', { class: 'btn', onclick: () => H.importPresets() }, '导入…'),
        h('button', { class: 'btn', onclick: () => H.exportCurrent() }, '导出当前'),
        h('button', { class: 'btn', onclick: () => H.exportAll() }, '导出全部'),
      ),
    );
  }

  refreshPresets(): void {
    const sel = this.presetSelect;
    const cur = this.host.settings().presetId;
    sel.innerHTML = '';
    sel.append(h('option', { value: 'track' }, `跟随曲目（${this.host.trackSceneName()}）`));
    const builtin = this.host.presets().filter((p) => p.builtin);
    const user = this.host.presets().filter((p) => !p.builtin);
    sel.append(h('optgroup', { label: '内置场景' }, ...builtin.map((p) => h('option', { value: p.id }, p.name))));
    if (user.length) sel.append(h('optgroup', { label: '我的预设' }, ...user.map((p) => h('option', { value: p.id }, p.name))));
    sel.value = cur;
    if (sel.value !== cur) sel.value = 'track';
  }

  private buildMonitor(b: HTMLElement): void {
    const names = ['响度包络', ...BANDS.map((x) => x.label), '频段合成', '最终驱动', '起音脉冲', '头部圆光', '周身金白', '轮廓光', '白毫柔光', '佛顶光'];
    const wrap = h('div', { class: 'meters' });
    for (const n of names) {
      const fill = h('div', { class: 'meter-fill' });
      const val = h('span', { class: 'meter-val' }, '0.00');
      wrap.append(h('div', { class: 'meter' }, h('span', { class: 'meter-name' }, n), h('div', { class: 'meter-bar' }, fill), val));
      this.meterEls.push({ fill, val });
    }
    this.metaEl = h('div', { class: 'hint mono' });
    b.append(wrap, this.metaEl);
  }

  updateMonitor(f: AudioFeatures, L: LightLevels, meta: string): void {
    if (!this.open) return;
    // 光源一栏显示亮度系数（满格 = 2）
    const vals = [f.loudness, ...f.bands, f.bandDrive, f.drive, f.onset, L.head / 2, L.aura / 2, L.contour / 2, L.urna / 2, L.crown / 2];
    vals.forEach((v, i) => {
      const m = this.meterEls[i];
      m.fill.style.transform = `scaleX(${Math.min(1, v)})`;
      m.val.textContent = (i >= 8 ? v * 2 : v).toFixed(2);
    });
    this.metaEl.textContent = meta;
    this.tlInfo.textContent = this.host.timelineInfo();
  }

  /** 场景整体替换后，同步全部控件 */
  syncAll(): void {
    for (const i of this.inputs) i.sync();
    this.refreshPresets();
    if (this.tlInfo) this.tlInfo.textContent = this.host.timelineInfo();
  }
}
